import styled from "styled-components";
import PaginatedTable from "../../Componentes/Utils/PaginatedTable";
import { useEffect, useState } from "react";
import useApi from "../../hooks/useApi";
import { useModal } from "../../contexts/ModalContext";
import ModalFiltersControladoria from "./ModalFiltersControladoria";
import ModalItensPedidoResumo from "./ModalItensPedidoResumo";
import { useToast } from "../../contexts/ToastContext";
import { useRegisterControladoriaPageActions } from "./layout";

const Container = styled.div`
    width: 100%;
    height: 100%;
    overflow: hidden;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
`;

const filtrosIniciais = {
    filial: "",
    numPedido: "",
    dataInicio: "",
    dataFim: ""
};

const Body = styled.div`
    flex: 1;
    width: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background-color: var(--ctrl-surface, #fff);
    margin-top: 5px;
    border-radius: 8px;
    border: 1.2px solid var(--ctrl-border, #d3d3d3);
    box-sizing: border-box;
`;

const LinhaTabela = styled.tr`
    cursor: pointer;
`;

const PedidosDeCompraResumo = () => {
    const api = useApi();
    const { notifyError } = useToast();
    const { isModalOpen, openModal, closeModal } = useModal();

    const [pagina, setPagina] = useState(1);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    const [dados, setDados] = useState([]);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [pedidoSelecionado, setPedidoSelecionado] = useState(null);
    const [itensPedido, setItensPedido] = useState([]);
    const [loadingItens, setLoadingItens] = useState(false);

    const [filtros, setFiltros] = useState(filtrosIniciais);

    const fetchDados = async (page = pagina, curFiltros = filtros) => {
        setLoading(true);

        try {
            const params = new URLSearchParams();
            params.append("page", page);
            params.append("limit", 50);

            if (curFiltros.filial) params.append("filial", curFiltros.filial);
            if (curFiltros.numPedido) params.append("num_pedido", curFiltros.numPedido);
            if (curFiltros.dataInicio) params.append("data_inicio", curFiltros.dataInicio);
            if (curFiltros.dataFim) params.append("data_fim", curFiltros.dataFim);

            const { data } = await api.get(`Painel/API/Controladoria/PedidosCompra/index.php?${params.toString()}`);
            setDados(data.data || []);
            setTotalItems(data.quantidade || 0);
            setTotalPages(data.total_pages || 0);
        } catch (e) {
            notifyError(e?.response?.data?.message || "Erro ao carregar pedidos de compra.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDados(pagina, filtros);
    }, [pagina]);

    const refreshData = async () => {
        if (refreshing) return;

        setRefreshing(true);
        await fetchDados(pagina, filtros);
        setRefreshing(false);
    };

    const aplicarFiltros = () => {
        setPagina(1);
        fetchDados(1, filtros);
        closeModal();
    };

    const removerFiltros = () => {
        setFiltros(filtrosIniciais);
        setPagina(1);
        fetchDados(1, filtrosIniciais);
        closeModal();
    };

    const openFilters = () => {
        openModal("controladoria-filtros-pedidos-resumo");
    };

    useRegisterControladoriaPageActions({
        onRefresh: refreshData,
        onFilter: openFilters,
        refreshing,
    });

    const abrirItensPedido = async (pedido) => {
        setPedidoSelecionado(pedido);
        setItensPedido([]);
        setLoadingItens(true);
        openModal("controladoria-itens-pedido-resumo");

        try {
            const params = new URLSearchParams();
            params.append("filial", pedido.FILIAL);
            params.append("num_pedido", pedido.NUM_PEDIDO);

            const { data } = await api.get(`Painel/API/Controladoria/PedidosCompra/Itens/index.php?${params.toString()}`);
            setItensPedido(data.data || []);
        } catch (e) {
            notifyError(e?.response?.data?.message || "Erro ao carregar itens do pedido.");
        } finally {
            setLoadingItens(false);
        }
    };

    return (
        <>
            <Container>
                <Body>
                    <PaginatedTable
                        pagina={pagina}
                        setPagina={setPagina}
                        loading={loading}
                        colunas={5}
                        totalItems={totalItems}
                        totalPages={totalPages}
                        mobileCards={dados.map((item) => ({
                            key: `${item.FILIAL}-${item.NUM_PEDIDO}-${item.DT_EMISSAO}`,
                            title: `Pedido ${item.NUM_PEDIDO} · Filial ${item.FILIAL}`,
                            meta: `${item.DT_EMISSAO || "—"} · ${item.FORNECEDOR || "Sem fornecedor"}${item.USUARIO ? ` · ${item.USUARIO}` : ""}`,
                            onClick: () => abrirItensPedido(item),
                        }))}
                        thead={
                            <tr>
                                <th>FILIAL</th>
                                <th>NÚMERO PEDIDO</th>
                                <th>DATA EMISSÃO</th>
                                <th>FORNECEDOR</th>
                                <th>USUÁRIO</th>
                            </tr>
                        }
                    >
                        <tbody>
                            {dados.map((item) => (
                                <LinhaTabela
                                    key={`${item.FILIAL}-${item.NUM_PEDIDO}-${item.DT_EMISSAO}`}
                                    onClick={() => abrirItensPedido(item)}
                                >
                                    <td>{item.FILIAL}</td>
                                    <td>{item.NUM_PEDIDO}</td>
                                    <td>{item.DT_EMISSAO}</td>
                                    <td>{item.FORNECEDOR || " - "}</td>
                                    <td>{item.USUARIO || " - "}</td>
                                </LinhaTabela>
                            ))}
                        </tbody>
                    </PaginatedTable>
                </Body>
            </Container>

            {isModalOpen("controladoria-filtros-pedidos-resumo") && (
                <ModalFiltersControladoria
                    fecharModal={closeModal}
                    filtros={filtros}
                    setFiltros={setFiltros}
                    aplicarFiltros={aplicarFiltros}
                    removerFiltros={removerFiltros}
                    fields={[
                        { name: "filial", label: "Filial:", type: "text" },
                        { name: "numPedido", label: "Número Pedido:", type: "text" },
                        { name: "dataInicio", label: "Data Emissão Inicial:", type: "date" },
                        { name: "dataFim", label: "Data Emissão Final:", type: "date" },
                    ]}
                />
            )}

            {isModalOpen("controladoria-itens-pedido-resumo") && (
                <ModalItensPedidoResumo
                    fecharModal={closeModal}
                    pedido={pedidoSelecionado}
                    itens={itensPedido}
                    loadingItens={loadingItens}
                />
            )}
        </>
    );
};

export default PedidosDeCompraResumo;
